type ShowSaveFilePickerWindow = Window & {
  showSaveFilePicker?: (options: {
    suggestedName: string
  }) => Promise<FileSystemFileHandle>
}

const downloadAsFile = (content: Blob, fileName: string) => {
  const a = document.createElement('a')
  const url = URL.createObjectURL(content)
  a.href = url
  a.download = fileName
  a.click()
  URL.revokeObjectURL(url)
}

type SaveFilePickerResult =
  | { status: 'selected'; handle: FileSystemFileHandle }
  | { status: 'unavailable' }
  | { status: 'cancelled' }

const requestSaveFileHandle = async (
  fileName: string,
): Promise<SaveFilePickerResult> => {
  const showSaveFilePicker = (window as ShowSaveFilePickerWindow)
    .showSaveFilePicker

  if (!showSaveFilePicker) {
    return { status: 'unavailable' }
  }

  try {
    const handle = await showSaveFilePicker({ suggestedName: fileName })
    return { status: 'selected', handle }
  } catch (e) {
    if (e instanceof DOMException && e.name === 'AbortError') {
      return { status: 'cancelled' }
    }
    throw e
  }
}

const writeToFileHandle = async (
  handle: FileSystemFileHandle,
  content: Blob,
) => {
  const writable = await handle.createWritable()
  await writable.write(content)
  await writable.close()
}

/**
 * Prompts the user to pick the save location/filename via the browser's native file picker, where supported.
 */
const downloadAsFileWithPrompt = async (content: Blob, fileName: string) => {
  const result = await requestSaveFileHandle(fileName)

  if (result.status === 'unavailable') {
    downloadAsFile(content, fileName)
    return
  }

  if (result.status === 'selected') {
    await writeToFileHandle(result.handle, content)
  }
}

export {
  downloadAsFile,
  downloadAsFileWithPrompt,
  requestSaveFileHandle,
  writeToFileHandle,
}
